import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import MaterialTable, { MTableToolbar } from 'material-table';
import { Autocomplete } from '@material-ui/lab';
import {
  Button,
  CardContent,
  Chip,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import WarningOutlinedIcon from '@material-ui/icons/WarningOutlined';
import ScheduleOutlinedIcon from '@material-ui/icons/ScheduleOutlined';
import CheckCircleOutlineOutlinedIcon from '@material-ui/icons/CheckCircleOutlineOutlined';
import AutorenewOutlinedIcon from '@material-ui/icons/AutorenewOutlined';
import { Search } from '@material-ui/icons';
import { localizationTable, options } from 'constants/table';
import { EnrollmentData, ReportCompanyData } from 'services/dataService';
import { toast } from 'react-toastify';
import MenuDownloadReport from 'components/MenuDownloadReport';
import { useStyles } from './styles';

const STATUS_FILTERS = {
  all: '',
  expired: 'expired',
  onTime: 'onTime',
  dueSoon: 'dueSoon',
};

const buildCardConfig = (summary) => [
  {
    key: 'expired',
    title: 'Vencidos',
    value: summary.expired,
    total: summary.total,
    label: 'Treinamentos Vencidos',
    color: '#E74C3C',
    background: '#FDEDEC',
    icon: <WarningOutlinedIcon fontSize="small" />,
  },
  {
    key: 'onTime',
    title: 'Em dia',
    value: summary.onTime,
    total: summary.total,
    label: 'Em dia',
    color: '#2EB67D',
    background: '#EAF7F2',
    icon: <CheckCircleOutlineOutlinedIcon fontSize="small" />,
  },
  {
    key: 'dueSoon',
    title: 'Vencendo',
    value: summary.dueSoon,
    total: summary.total,
    label: 'Vencendo em até 30 dias',
    color: '#F39C12',
    background: '#FEF5E7',
    icon: <ScheduleOutlinedIcon fontSize="small" />,
  },
  {
    key: 'rematriculated',
    title: 'Rematriculados',
    value: summary.rematriculated,
    total: summary.total,
    label: 'Treinamentos Rematriculados',
    color: '#3F8CFF',
    background: '#EAF2FF',
    icon: <AutorenewOutlinedIcon fontSize="small" />,
  },
];

export default () => {
  const classes = useStyles();
  const tableRef = useRef();
  const filterRef = useRef('');
  const { t } = useTranslation(['button']);
  const { register, handleSubmit } = useForm();
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState(STATUS_FILTERS.all);
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [isDisregarding, setIsDisregarding] = useState(false);
  const [summary, setSummary] = useState({
    total: 0,
    expired: 0,
    dueSoon: 0,
    onTime: 0,
    rematriculated: 0,
  });

  useEffect(() => {
    const loadCourses = async () => {
      try {
        const response = await EnrollmentData.getCompanyCourseComboRecycling();
        const normalizedCourses = (response?.data?.courses || []).map(course => ({
          id: course?.id,
          title: course?.course?.name || course?.name || '',
        }));
        setCourses(normalizedCourses);
      } catch (error) {
        toast.error('Não foi possível carregar os cursos para o filtro.');
      }
    };

    loadCourses();
  }, []);

  const cards = useMemo(() => buildCardConfig(summary), [summary]);

  const handleSearch = data => {
    const nextFilter = data.filter || '';
    setFilter(nextFilter);
    filterRef.current = nextFilter;

    if (tableRef.current) {
      tableRef.current.state.query.page = 0;
      tableRef.current.onQueryChange();
    }
  };

  const handleFetch = async () => {
    try {
      const response = await ReportCompanyData.getAcademicRecycling({
        page: 1,
        limit: 99999,
        filter: filterRef.current,
        id_company_course: selectedCourse?.id,
        recycle_status: statusFilter,
      });

      return {
        dataToExport: response?.data?.academicRecycling || [],
      };
    } catch (error) {
      toast.error('Não foi possível baixar o relatório de reciclagem.');
      return {
        dataToExport: [],
      };
    }
  };

  const getStatusChip = status => {
    if (status === 'Vencido') {
      return <Chip label={status} size="small" style={{ backgroundColor: '#FDEDEC', color: '#E74C3C' }} className={classes.statusChip} />;
    }

    if (status === 'Vence em até 30 dias') {
      return <Chip label={status} size="small" style={{ backgroundColor: '#FEF5E7', color: '#F39C12' }} className={classes.statusChip} />;
    }

    return <Chip label={status} size="small" style={{ backgroundColor: '#EAF7F2', color: '#2EB67D' }} className={classes.statusChip} />;
  };

  const handleDisregard = async (rowData) => {
    const confirmed = window.confirm(
      `Deseja desconsiderar a reciclagem do curso "${rowData?.course_name}" para "${rowData?.student_name}"?`
    );

    if (!confirmed) {
      return;
    }

    try {
      setIsDisregarding(true);
      await ReportCompanyData.disregardAcademicRecycling(rowData.id_enrollment);
      toast.success('Reciclagem desconsiderada com sucesso.');

      if (tableRef.current) {
        tableRef.current.onQueryChange();
      }
    } catch (error) {
      toast.error('Não foi possível desconsiderar a reciclagem.');
    } finally {
      setIsDisregarding(false);
    }
  };

  return (
    <div className="notranslate">
      <MaterialTable
        tableRef={tableRef}
        title={
          <div style={{ marginTop: 15, marginBottom: 15 }}>
            <Typography variant="h6">
              Relatório de Reciclagem
            </Typography>
            <Typography variant="subtitle2" style={{ fontSize: 12 }}>
              Acompanhe treinamentos vencidos e próximos ao vencimento. Use os filtros para localizar por curso, aluno ou status.
            </Typography>
            <Typography variant="subtitle2" style={{ fontSize: 12 }}>
             ⚠️ ATENÇÃO: Após o vencimento do treinamento, o aluno será rematriculado automaticamente no curso e receberá um e-mail informando sobre a nova matrícula. Após a rematrícula, o treinamento anterior permanecerá disponível no histórico acadêmico.
            </Typography>
          </div>
        }
        columns={[
          { title: 'Curso', field: 'course_name', sorting: false },
          { title: 'Usuário', field: 'student_name', sorting: false },
          { title: 'E-mail', field: 'student_email', sorting: false },
          { title: 'Data matrícula', field: 'enroll_created_at', sorting: false },
          { title: 'Data Finalização', field: 'finished_at', sorting: false },
          { title: 'Vencimento', field: 'recycle_due_at', sorting: false },
          { title: 'CPF', field: 'student_cpf', sorting: false },
          // { title: 'Time', field: 'teams', sorting: false },
          // { title: 'Trilha', field: 'trail_name', sorting: false },
          // {
          //   title: 'Progresso',
          //   field: 'progress_value',
          //   sorting: false,
          //   render: rowData => (
          //     <div className={classes.progressWrapper}>
          //       <div className={classes.progressTrack}>
          //         <div
          //           className={classes.progressBar}
          //           style={{
          //             width: `${rowData.progress_value || 0}%`,
          //             backgroundColor: '#3F61E8',
          //           }}
          //         />
          //       </div>
          //       <span className={classes.progressText}>{rowData.percent_progress}</span>
          //     </div>
          //   ),
          // },
          // { title: 'Nota', field: 'grade', sorting: false },
          {
            title: 'Status',
            field: 'status_label',
            sorting: false,
            render: rowData => getStatusChip(rowData.status_label),
          },
          {
            title: 'Reciclagem',
            field: 'recycle_status',
            sorting: false,
            render: rowData => (
              <Chip
                label={rowData.recycle_status}
                size="small"
                style={{ backgroundColor: '#EAF7F2', color: '#2EB67D' }}
                className={classes.statusChip}
              />
            ),
          },
          {
            title: 'Desconsiderar',
            field: 'disregard',
            sorting: false,
            render: rowData => (
              <Button
                variant="outlined"
                size="small"
                disabled={isDisregarding}
                onClick={() => handleDisregard(rowData)}
                className={classes.actionButton}
              >
                Desconsiderar
              </Button>
            ),
          },
        ]}
        data={async (query) => {
          try {
            const response = await ReportCompanyData.getAcademicRecycling({
              page: query.page + 1,
              limit: query.pageSize,
              filter: filterRef.current,
              id_company_course: selectedCourse?.id,
              recycle_status: statusFilter,
            });

            const {
              academicRecycling,
              currentPage,
              totalCount,
              summary: responseSummary,
            } = response.data;

            setSummary(responseSummary || {
              total: 0,
              expired: 0,
              dueSoon: 0,
              onTime: 0,
              rematriculated: 0,
            });

            return {
              data: academicRecycling || [],
              page: (currentPage || 1) - 1,
              totalCount: totalCount || 0,
            };
          } catch (error) {
            setSummary({
              total: 0,
              expired: 0,
              dueSoon: 0,
              onTime: 0,
              rematriculated: 0,
            });
            toast.error('Não foi possível carregar o relatório de reciclagem.');
            return {
              data: [],
              page: 0,
              totalCount: 0,
            };
          }
        }}
        components={{
          Toolbar: (props) => (
            <div style={{ position: 'relative' }}>
              <MTableToolbar {...props} />
              <CardContent style={{ marginBottom: 12, paddingBottom: 0 }}>
                <Grid container spacing={2}>
                  {cards.map(card => {
                    const percent = card.total ? Math.round((card.value / card.total) * 100) : 0;

                    return (
                      <Grid item xs={12} md={3} key={card.key}>
                        <Paper variant="outlined" className={classes.summaryCard}>
                          <Grid container justify="space-between" alignItems="center">
                            <Typography variant="subtitle2" className={classes.summaryTitle}>
                              {card.title}
                            </Typography>
                            <div
                              className={classes.summaryIcon}
                              style={{ backgroundColor: card.background, color: card.color }}
                            >
                              {card.icon}
                            </div>
                          </Grid>

                          <div className={classes.summaryValueRow}>
                            <Typography variant="h4" className={classes.summaryValue} style={{ color: card.color }}>
                              {card.value}
                            </Typography>
                            <Typography variant="h6" className={classes.summaryTotal}>
                              / {card.total}
                            </Typography>
                          </div>

                          <Typography variant="body2" className={classes.summaryLabel}>
                            {card.label}
                          </Typography>

                          <div className={classes.progressTrack}>
                            <div
                              className={classes.progressBar}
                              style={{
                                width: `${percent}%`,
                                backgroundColor: card.color,
                              }}
                            />
                          </div>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              </CardContent>
              <Divider />
              <form onSubmit={handleSubmit(handleSearch)}>
                <CardContent>
                  <Grid container spacing={1}>
                    <Grid item md={4} xs={12}>
                      <TextField
                        fullWidth
                        label="Pesquisar"
                        name="filter"
                        type="text"
                        variant="outlined"
                        defaultValue={filter}
                        inputRef={register}
                      />
                    </Grid>
                    <Grid item md={4} xs={12}>
                      <Autocomplete
                        options={courses}
                        getOptionLabel={(option) => option?.title || ''}
                        value={selectedCourse}
                        onChange={(_, value) => {
                          setSelectedCourse(value);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Curso"
                            variant="outlined"
                          />
                        )}
                      />
                    </Grid>
                    <Grid item md={2} xs={12}>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel id="recycling-status-filter">
                          Status
                        </InputLabel>
                        <Select
                          labelId="recycling-status-filter"
                          label="Status"
                          value={statusFilter}
                          onChange={(event) => setStatusFilter(event.target.value)}
                        >
                          <MenuItem value={STATUS_FILTERS.all}>Todos status</MenuItem>
                          <MenuItem value={STATUS_FILTERS.expired}>Vencido</MenuItem>
                          <MenuItem value={STATUS_FILTERS.onTime}>Em dia</MenuItem>
                          <MenuItem value={STATUS_FILTERS.dueSoon}>Vencendo em 30 dias</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item md={2} xs={12} className={classes.filterActions}>
                      <Button
                        color="primary"
                        variant="contained"
                        size="large"
                        type="submit"
                      >
                        <Search className={classes.icon} />
                        Buscar
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </form>
            </div>
          ),
        }}
        localization={localizationTable(t)}
        actions={[
          {
            icon: () => (
              <MenuDownloadReport
                handleFetch={handleFetch}
                tableRef={tableRef}
                fileName="RelatorioReciclagem"
                hasCSV
              />
            ),
            isFreeAction: true,
          },
        ]}
        options={{
          ...options,
          exportButton: false,
          doubleHorizontalScroll: 'true',
        }}
      />
    </div>
  );
};
